import Axios, {AxiosRequestConfig, AxiosResponse, AxiosInstance} from 'axios';
import {
    BceRequestConfig,
    BceResponse,
    HttpConfig,
    QualifyResponse,
    BceResponseResult,
    BceResponsePage,
} from '@/types/http';
import {REFERRER_REQUEST} from '@/dicts/common';
import {getUrlWithQuery} from '@/utils/util';
import {getOcpcParam} from './ThirdParty/ocpc';
import {getAddNewParam} from './ThirdParty/addNew';
import {getNeedActivatedParam} from './ThirdParty/isActivated';
import {getNeedFromHeader} from './ThirdParty/from';
import {getNeedRiskHeader} from './ThirdParty/riskSdk';
import {getNeedProductHeader} from './ThirdParty/product';

async function getPostExtraParams(url: string) {
    const ocpcParams = await getOcpcParam(url);
    const addNewParams = await getAddNewParam(url);
    const isActivatedParams = await getNeedActivatedParam(url);
    return {
        ...ocpcParams,
        ...addNewParams,
        ...isActivatedParams,
    };
}

async function getPostExtraHeader(url: string) {
    const fromHeader = getNeedFromHeader(url);
    const riskHeader = await getNeedRiskHeader(url);
    const productHeader = getNeedProductHeader(url);
    const headers = {
        ...(fromHeader || {}),
        ...(riskHeader || {}),
        ...(productHeader || {}),
    };
    return Object.keys(headers).length ? headers : null;
}

export class Http {
    $http: AxiosInstance;
    baseConfig: HttpConfig = {};

    constructor(config: HttpConfig = {}) {
        this.baseConfig = config;
        this.$http = Axios.create({
            headers: config.headers,
        });

        this.$http.interceptors.request.use((reqConfig: AxiosRequestConfig) => {
            if (!config.silent && config.loading) {
                config?.loadingHandler?.create();
            }
            // 为特殊请求增加referrer信息
            if (REFERRER_REQUEST.includes(reqConfig.url as string)) {
                const referrer = document.referrer;
                reqConfig.headers = {
                    ...reqConfig.headers,
                    'X-Bce-Qualify-Refer': referrer,
                };
            }
            return reqConfig;
        });
        this.$http.interceptors.response.use(
            (response: AxiosResponse) => {
                !config.silent && config.loading && config?.loadingHandler?.clear();
                return response;
            },
            (error: AxiosResponse) => {
                !config.silent && config.loading && config?.loadingHandler?.clear();
                return error;
            }
        );
    }

    get<T>(url: string, query?: any, config?: BceRequestConfig): Promise<T> {
        const path = query ? getUrlWithQuery(url, {
            ...query,
        }) : url;
        return this.$http
            .get<QualifyResponse<T>>(path, config)
            .then((response: AxiosResponse<QualifyResponse<T>>) => this.handleResponse.bind(this)(response, config));
    }

    post<T>(url: string, data?: any, config?: BceRequestConfig): Promise<T> {
        return getPostExtraParams(url).then(async extra => {
            const newBody = extra && Object.keys(extra).length ? {
                ...data,
                ...extra,
            } : data;
            const extraHeader = await getPostExtraHeader(url);
            const newConfig = config ? {...config} : {};
            if (extraHeader) {
                newConfig.headers = {
                    ...(newConfig.headers || {}),
                    ...extraHeader,
                };
            }
            return this.$http
                .post<QualifyResponse<T>>(url, newBody, newConfig)
                .then((response: AxiosResponse<QualifyResponse<T>>) =>
                    this.handleResponse.bind(this)(response, config));
        });
    }

    delete<T>(url: string, config?: BceRequestConfig): Promise<T> {
        return this.$http
            .delete<QualifyResponse<T>>(url, config)
            .then((response: AxiosResponse<QualifyResponse<T>>) => this.handleResponse.bind(this)(response, config));
    }

    put<T>(url: string, data?: any, config?: BceRequestConfig): Promise<T> {
        return this.$http
            .put<QualifyResponse<T>>(url, data, config)
            .then((response: AxiosResponse<QualifyResponse<T>>) => this.handleResponse.bind(this)(response, config));
    }

    patch<T>(url: string, data?: any, config?: BceRequestConfig): Promise<T> {
        return this.$http
            .patch<QualifyResponse<T>>(url, data, config)
            .then((response: AxiosResponse<QualifyResponse<T>>) => this.handleResponse.bind(this)(response, config));
    }

    private handleResponse<T>(
        response: AxiosResponse<QualifyResponse<T>>,
        config?: BceRequestConfig | HttpConfig | undefined
    ): Promise<T> {
        if (this.isResponseError<T>(response)) {
            if (!config?.silent) {
                this.baseConfig?.errorHandler && this.baseConfig?.errorHandler(response.data, config);
            }

            return Promise.reject(response.data);
        }

        if (this.isBceResponse<T>(response)) {
            return this.resolveData<T>(response);
        }

        return Promise.resolve(response.data as T);
    }

    private resolveData<T>(response: AxiosResponse<QualifyResponse<T>>) {
        const data = response.data as BceResponseResult<T>;
        const result = data.result === undefined
            ? (response.data as BceResponsePage<T>).page
            : data.result;
        return Promise.resolve(result);
    }

    private isResponseError<T>(response: AxiosResponse<QualifyResponse<T>>): boolean {
        if (response.status !== 200) {
            return true;
        }

        if (response.config.responseType === 'blob') {
            return false;
        }

        const dataSuccess = response?.data && (response.data as BceResponse).success;
        // success 有可能返回字符串 "false"
        const success = typeof dataSuccess === 'boolean' ? dataSuccess : dataSuccess === 'true';
        return !!((response?.data && (response?.data as BceResponse).status !== 200
            && !success)
        );
    }

    private isBceResponse<T>(response: AxiosResponse<QualifyResponse<T>>): boolean {
        return (response?.data as any).result !== undefined || (response?.data as any).page !== undefined;
    }
}
